// =========================================================
// ARMONIA MOBILE STICKY ADD TO CART
// Robust scroll-based visibility + Dawn native add-to-cart.
// =========================================================

if (!customElements.get('armonia-sticky-atc')) {
  customElements.define(
    'armonia-sticky-atc',
    class ArmoniaStickyAtc extends HTMLElement {
      connectedCallback() {
        // A fixed element can be trapped by transformed/sticky ancestors.
        // Move it directly under body before initializing.
        if (this.parentElement !== document.body) {
          if (this.dataset.armoniaMoving === 'true') return;
          this.dataset.armoniaMoving = 'true';
          document.body.appendChild(this);
          return;
        }

        delete this.dataset.armoniaMoving;
        if (this.initialized) return;

        this.sectionId = this.dataset.sectionId || '';
        if (!this.sectionId) return;

        this.productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
        if (!this.productInfo) return;

        this.stickyButton = this.querySelector('[data-armonia-sticky-button]');
        this.stickyLabel = this.querySelector('[data-armonia-sticky-label]');
        this.stickyPrice = this.querySelector('[data-armonia-sticky-price]');
        this.stickyCompare = this.querySelector('[data-armonia-sticky-compare]');

        if (!this.stickyButton || !this.stickyLabel || !this.stickyPrice || !this.stickyCompare) return;

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.stickyButton.addEventListener(
          'click',
          () => {
            const mainButton = this.getMainButton();
            if (!mainButton || this.isMainButtonDisabled(mainButton)) return;
            mainButton.click();
            this.syncState();
          },
          { signal },
        );

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event?.source !== 'product-form') return;
            this.showAddedState();
          });
        }

        this.productInfo.addEventListener(
          'change',
          () => this.queueUpdate(),
          { signal },
        );

        window.addEventListener('scroll', () => this.queueVisibilityCheck(), { passive: true, signal });
        window.addEventListener('resize', () => this.queueUpdate(), { passive: true, signal });
        window.addEventListener('pageshow', () => this.queueUpdate(), { passive: true, signal });

        this.mutationObserver = new MutationObserver(() => this.queueUpdate());
        this.mutationObserver.observe(this.productInfo, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden'],
        });

        this.previewObserver = new MutationObserver(() => this.updatePreviewOffset());
        this.previewObserver.observe(document.body, { childList: true, subtree: false });

        this.initialized = true;
        this.hidden = false;
        this.syncState();
        this.updatePreviewOffset();
        this.evaluateVisibility();
      }

      disconnectedCallback() {
        if (this.dataset.armoniaMoving === 'true') return;

        this.abortController?.abort();
        this.mutationObserver?.disconnect();
        this.previewObserver?.disconnect();
        this.cartUpdateUnsubscriber?.();
        window.clearTimeout(this.addedTimer);

        if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
        if (this.visibilityFrame) cancelAnimationFrame(this.visibilityFrame);

        document.body.classList.remove('armonia-sticky-atc-visible');
        this.initialized = false;
      }

      getMainButton() {
        return (
          document.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
          this.productInfo?.querySelector('.product-form__submit[name="add"], .product-form__submit[type="submit"]') ||
          null
        );
      }

      getPriceRoot() {
        return (
          document.getElementById(`price-${this.sectionId}`) ||
          this.productInfo?.querySelector('[id^="price-"]') ||
          null
        );
      }

      isMainButtonDisabled(button) {
        return !button || button.disabled || button.getAttribute('aria-disabled') === 'true';
      }

      getVisibleText(element) {
        if (!element) return '';
        return (element.textContent || '').replace(/\s+/g, ' ').trim();
      }

      readCurrentPrice() {
        const root = this.getPriceRoot();
        if (!root) return { current: '', compare: '' };

        const price = root.querySelector('.price');
        const isOnSale = price?.classList.contains('price--on-sale');
        let current = '';
        let compare = '';

        if (isOnSale) {
          current = this.getVisibleText(root.querySelector('.price__sale .price-item--sale'));
          compare = this.getVisibleText(root.querySelector('.price__sale .price-item--regular'));
        } else {
          current = this.getVisibleText(root.querySelector('.price__regular .price-item--regular'));
        }

        if (!current) current = this.getVisibleText(root.querySelector('.price-item:not(.price-item--regular)'));
        if (!current) current = this.getVisibleText(root.querySelector('.price-item'));

        return { current, compare };
      }

      syncState() {
        if (!this.initialized) return;

        const mainButton = this.getMainButton();
        if (!mainButton) {
          this.hideSticky();
          return;
        }

        const disabled = this.isMainButtonDisabled(mainButton);
        const loading = mainButton.classList.contains('loading');

        this.stickyButton.disabled = disabled || loading;
        this.stickyButton.classList.toggle('is-loading', loading);
        this.stickyButton.setAttribute('aria-busy', loading ? 'true' : 'false');

        const mainLabel = this.getVisibleText(mainButton.querySelector('span')) || this.getVisibleText(mainButton);
        if (!this.stickyButton.classList.contains('is-added')) {
          this.stickyLabel.textContent = disabled ? (mainLabel || 'Rasprodato') : 'Dodaj u korpu';
        }

        const { current, compare } = this.readCurrentPrice();
        if (current) this.stickyPrice.textContent = current;

        if (compare && compare !== current) {
          this.stickyCompare.textContent = compare;
          this.stickyCompare.hidden = false;
        } else {
          this.stickyCompare.textContent = '';
          this.stickyCompare.hidden = true;
        }
      }

      queueUpdate() {
        if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
        this.updateFrame = requestAnimationFrame(() => {
          this.updateFrame = null;
          this.syncState();
          this.updatePreviewOffset();
          this.evaluateVisibility();
        });
      }

      queueVisibilityCheck() {
        if (this.visibilityFrame) return;
        this.visibilityFrame = requestAnimationFrame(() => {
          this.visibilityFrame = null;
          this.evaluateVisibility();
        });
      }

      evaluateVisibility() {
        if (!this.initialized) return;

        if (!window.matchMedia('(max-width: 749px)').matches) {
          this.hideSticky();
          return;
        }

        const mainButton = this.getMainButton();
        if (!mainButton) {
          this.hideSticky();
          return;
        }

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer?.classList.contains('active')) {
          this.hideSticky();
          return;
        }

        const rect = mainButton.getBoundingClientRect();

        // Follow the shopper as soon as the native CTA has effectively left the usable viewport.
        if (rect.bottom <= 72) {
          this.showSticky();
        } else {
          this.hideSticky();
        }
      }

      updatePreviewOffset() {
        let previewHeight = 0;
        const candidates = [
          document.getElementById('preview-bar-iframe'),
          document.querySelector('iframe[src*="preview-bar"]'),
          document.querySelector('iframe[id*="preview-bar"]'),
          document.querySelector('[id*="preview-bar"]'),
        ].filter(Boolean);

        for (const element of candidates) {
          const rect = element.getBoundingClientRect();
          if (rect.height > 20 && rect.bottom >= window.innerHeight - 4) {
            previewHeight = Math.max(previewHeight, Math.ceil(rect.height));
          }
        }

        this.style.setProperty('--armonia-sticky-preview-offset', `${previewHeight}px`);
      }

      showAddedState() {
        if (!this.stickyButton || !this.stickyLabel) return;

        this.stickyButton.classList.add('is-added');
        this.stickyLabel.textContent = 'Dodato ✓';

        window.clearTimeout(this.addedTimer);
        this.addedTimer = window.setTimeout(() => {
          this.stickyButton.classList.remove('is-added');
          this.syncState();
        }, 1400);
      }

      showSticky() {
        this.classList.add('is-visible');
        document.body.classList.add('armonia-sticky-atc-visible');
      }

      hideSticky() {
        this.classList.remove('is-visible');
        document.body.classList.remove('armonia-sticky-atc-visible');
      }
    },
  );
}
